import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Toaster } from 'sonner';
import { AuthGuard } from '@/components/auth/auth-guard';
import { AppLayout } from '@/components/layout/app-layout';
import { useNetworkStatus } from '@/hooks/use-network-status';
import '@/modules/saas/sdk-init';

// Retry wrapper — on chunk load failure (e.g. stale hash after deploy), reload the page once
function lazyWithRetry(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    factory().catch((err) => {
      const reloaded = sessionStorage.getItem('chunk_reload');
      if (!reloaded) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
        return new Promise(() => {});
      }
      sessionStorage.removeItem('chunk_reload');
      throw err;
    })
  );
}

// Critical path — keep static (auth + main app shell)
import { LoginPage } from '@/pages/login-page';
import { LogoutPage } from '@/pages/logout-page';
import { OAuthPage } from '@/pages/oauth-page';
import { OAuthWosPage } from '@/pages/oauth-wos-page';
import { AuthorizationSuccessPage } from '@/pages/authorization-success-page';
import { LoginEnsurePage } from '@/pages/login-ensure-page';
import { AppPage } from '@/pages/app-page';
const AppLitePage = lazyWithRetry(() => import('@/pages/app-lite-page').then(m => ({ default: m.AppLitePage })));
import { InboxPickerPage } from '@/pages/inbox-picker-page';
import { NotificationClickPage } from '@/pages/notification-click-page';
import { OfflinePage } from '@/pages/offline-page';
import { NotFoundPage } from '@/pages/not-found-page';

// Lazy-loaded pages — split into separate chunks
const DashboardLayout = lazyWithRetry(() => import('@/components/layout/dashboard-layout').then(m => ({ default: m.DashboardLayout })));
const DashboardPage = lazyWithRetry(() => import('@/pages/dashboard-page').then(m => ({ default: m.DashboardPage })));
const IncomingTrafficPage = lazyWithRetry(() => import('@/pages/incoming-traffic-page').then(m => ({ default: m.IncomingTrafficPage })));
const CreditClaimsPage = lazyWithRetry(() => import('@/pages/credit-claims-page').then(m => ({ default: m.CreditClaimsPage })));
const CreditLoansPage = lazyWithRetry(() => import('@/pages/credit-loans-page').then(m => ({ default: m.CreditLoansPage })));
const StorePage = lazyWithRetry(() => import('@/pages/store-page').then(m => ({ default: m.StorePage })));
const GuideAppDocV3Page = lazyWithRetry(() => import('@/pages/guide-app-doc-v3-page').then(m => ({ default: m.GuideAppDocV3Page })));
const GuideAppsV3IndexPage = lazyWithRetry(() => import('@/pages/guide-apps-v3-index-page').then(m => ({ default: m.GuideAppsV3IndexPage })));
const GuideFeatureDocPage = lazyWithRetry(() => import('@/pages/guide-feature-doc-page').then(m => ({ default: m.GuideFeatureDocPage })));
const GuideFeaturesIndexPage = lazyWithRetry(() => import('@/pages/guide-features-index-page').then(m => ({ default: m.GuideFeaturesIndexPage })));
const Guide3FlowchartPage = lazyWithRetry(() => import('@/pages/guide3-flowchart-page').then(m => ({ default: m.Guide3FlowchartPage })));
const MarketplacePage = lazyWithRetry(() => import('@/pages/marketplace-page').then(m => ({ default: m.MarketplacePage })));
const AiActionsPage = lazyWithRetry(() => import('@/pages/ai-actions').then(m => ({ default: m.AiActionsPage })));
const IntranetPage = lazyWithRetry(() => import('@/pages/intranet-page').then(m => ({ default: m.IntranetPage })));
const AdminPanelPage = lazyWithRetry(() => import('@/pages/admin-panel-page').then(m => ({ default: m.AdminPanelPage })));
const NotificationSettingsPage = lazyWithRetry(() => import('@/pages/notification-settings-page').then(m => ({ default: m.NotificationSettingsPage })));
const RedirectToCheckoutPage = lazyWithRetry(() => import('@/pages/redirect-to-checkout-page').then(m => ({ default: m.RedirectToCheckoutPage })));
const Bitrix24InstallPage = lazyWithRetry(() => import('@/pages/bitrix24-install-page').then(m => ({ default: m.Bitrix24InstallPage })));
const PrivacyPage = lazyWithRetry(() => import('@/pages/privacy-page').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazyWithRetry(() => import('@/pages/terms-page').then(m => ({ default: m.TermsPage })));
const StatusPage = lazyWithRetry(() => import('@/pages/status-page').then(m => ({ default: m.StatusPage })));
const ExtensionLoginPage = lazyWithRetry(() => import('@/pages/extension-login-page').then(m => ({ default: m.ExtensionLoginPage })));

const LazyFallback = () => (
  <div className="flex h-screen items-center justify-center">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

export default function App() {
  const isOnline = useNetworkStatus();

  return (
    <>
    <Toaster position="top-right" richColors closeButton />

    {/* Offline banner */}
    {!isOnline && (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-destructive text-destructive-foreground text-center text-xs font-medium py-1.5">
        You are offline. Some features may be unavailable.
      </div>
    )}
    <Suspense fallback={<LazyFallback />}>
    <Routes>
      {/* Auth */}
      <Route path="/login" element={<LoginPage />} />
      {/* Opened in its own tab by the browser extension — see extension-login-page.tsx */}
      <Route path="/extension-login" element={<ExtensionLoginPage />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/oauth-callback" element={<OAuthPage />} />
      <Route path="/oauth-home" element={<OAuthPage />} />
      {/* Shopify apps whitelist the legacy `/oauth-home.html` redirect_uri */}
      <Route path="/oauth-home.html" element={<OAuthPage />} />
      <Route path="/oauth-home/*" element={<OAuthPage />} />
      <Route path="/oauth-home-wos" element={<OAuthWosPage />} />
      <Route path="/authorization-success" element={<AuthorizationSuccessPage />} />
      <Route path="/login-ensure" element={<LoginEnsurePage />} />

      {/* Main app - protected */}
      <Route
        element={
          <AuthGuard>
            <AppLayout />
          </AuthGuard>
        }
      >
        <Route path="/send-sms" element={<AppPage />} />
        <Route path="/send-sms.html" element={<AppPage />} />
        <Route path="/app" element={<AppPage />} />
        {/* Backward compat: original Bitrix24 handler redirects to app.html */}
        <Route path="/app.html" element={<AppPage />} />
        <Route path="/compose/:appName" element={<AppPage />} />
        <Route path="/settings/:appName" element={<AppPage />} />
        <Route path="/calls/:appName" element={<AppPage />} />
        <Route path="/voicemail/:appName" element={<AppPage />} />
        <Route path="/recordings/:appName" element={<AppPage />} />
        <Route path="/call-queues/:appName" element={<AppPage />} />
        <Route path="/call-history/:appName" element={<AppPage />} />
        {/* Universal: /{appCode}/{viewPath} */}
        <Route path="/:appName/inbox" element={<AppPage />} />
        <Route path="/:appName/inbox/kanban" element={<AppPage />} />
        <Route path="/:appName/compose" element={<AppPage />} />
        <Route path="/:appName/settings" element={<AppPage />} />
        <Route path="/:appName/calls" element={<AppPage />} />
        <Route path="/:appName/voicemail" element={<AppPage />} />
        <Route path="/:appName/recordings" element={<AppPage />} />
        <Route path="/:appName/call-queues" element={<AppPage />} />
        <Route path="/:appName/call-history" element={<AppPage />} />
        <Route path="/:appName/schedule" element={<AppPage />} />
        <Route path="/:appName/history" element={<AppPage />} />
        <Route path="/:appName/links" element={<AppPage />} />
        {/* Legacy event paths */}
        <Route path="/schedule/:appName" element={<AppPage />} />
        <Route path="/history/:appName" element={<AppPage />} />
        <Route path="/links/:appName" element={<AppPage />} />
        <Route path="/app-lite" element={<AppLitePage />} />
<Route path="/lookupframe" element={<AppPage />} />
        <Route path="/inbox/:appName" element={<AppPage />} />
        <Route path="/inbox/:appName/kanban" element={<AppPage />} />
      </Route>

      {/* Inbox picker — shows installed inbox apps or auto-redirects */}
      <Route
        path="/inbox"
        element={
          <AuthGuard>
            <InboxPickerPage />
          </AuthGuard>
        }
      />

        {/* AI Actions */}
        <Route path="/ai-actions"
          element={
            <AuthGuard>
              <AiActionsPage />
            </AuthGuard>
          }
        />

      {/* Marketplace - public */}
      <Route path="/" element={<MarketplacePage />} />
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/marketplace/:saas" element={<MarketplacePage />} />

      {/* Admin dashboard - protected + admin role */}
      <Route
        element={
          <AuthGuard requireAdmin>
            <DashboardLayout />
          </AuthGuard>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/dashboard/incoming-traffic" element={<IncomingTrafficPage />} />
        <Route path="/dashboard/credit-claims" element={<CreditClaimsPage />} />
        <Route path="/dashboard/credit-loans" element={<CreditLoansPage />} />
        <Route path="/dashboard/*" element={<DashboardPage />} />
      </Route>

      {/* Store */}
      <Route path="/store" element={<StorePage />} />

      {/* Help docs - public; auth-aware sidebar shows "Your installed apps" when signed in */}
      {/* v3 (data-driven, in-SaaS, agent-authored) docs — now the default guide. */}
      <Route path="/guide/apps" element={<GuideAppsV3IndexPage />} />
      <Route path="/guide/apps/:appcode" element={<GuideAppDocV3Page />} />
      {/* Cross-app feature docs (field mapping, …) — src/content/v3/features/*.md */}
      <Route path="/guide/features" element={<GuideFeaturesIndexPage />} />
      <Route path="/guide/features/:slug" element={<GuideFeatureDocPage />} />
      {/* Message Credits lives at a short canonical URL; the /features/ form redirects. */}
      <Route path="/guide/message-credits" element={<GuideFeatureDocPage slug="message-credits" />} />
      <Route path="/guide/features/message-credits" element={<Navigate to="/guide/message-credits" replace />} />
      {/* Visual plan for the overnight unattended doc-build batch. */}
      <Route path="/guide/flowchart" element={<Guide3FlowchartPage />} />

      {/* Notifications */}
      <Route path="/notification-settings" element={<NotificationSettingsPage />} />
      <Route path="/notification-click" element={<NotificationClickPage />} />
      <Route path="/app-notification-click" element={<NotificationClickPage />} />

      {/* Integrations */}
      <Route path="/bitrix24-install" element={<Bitrix24InstallPage />} />
      {/* Backward compat: original used .html extension for BX24 marketplace */}
      <Route path="/bitrix24-install.html" element={<Bitrix24InstallPage />} />
      <Route path="/redirect-to-checkout" element={<RedirectToCheckoutPage />} />

      {/* Admin - internal */}
      <Route
        path="/intranet"
        element={
          <AuthGuard requireAdmin>
            <IntranetPage />
          </AuthGuard>
        }
      />
      <Route
        path="/admin"
        element={
          <AuthGuard requireAdmin>
            <AdminPanelPage />
          </AuthGuard>
        }
      />

      {/* Static pages */}
      <Route path="/privacy-policy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/offline" element={<OfflinePage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
    </>
  );
}
